import React, { useState } from 'react';
import { LockOutlined, UserOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Button, Form, Input, Card, Typography, Alert, Divider, message, Space } from 'antd';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { getBaseURL } from '../config.js';

const { Title, Text } = Typography;

const Login = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const baseURL = getBaseURL();
    
    const onFinish = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post('http://localhost:8080/api/auth/login', {
                username: values.username,
                password: values.password
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                const { token, user } = response.data.data;
                localStorage.setItem('token', token);
                message.success(response.data.message || 'Login successful!');
                navigate('/course-selection');
            } else {
                message.error(response.data.message || 'Login failed');
            }
        } catch (error) {
            console.error('Login error:', error);
            if (error.response?.data?.error) {
                const errorCode = error.response.data.error;
                const errorMessage = error.response.data.message;
                
                switch (errorCode) {
                    case 'INVALID_CREDENTIALS':
                        message.error('Invalid username or password');
                        break;
                    case 'VALIDATION_ERROR':
                        message.error('Please check your input data');
                        break;
                    default:
                        message.error(errorMessage || 'Login failed');
                }
            } else {
                message.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 16,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    border: 'none'
                }}
                bodyStyle={{ padding: '40px' }}
            >
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <Title level={2} style={{ marginBottom: 8, color: '#1890ff' }}>
                        🌟 Galaxy Mentor
                    </Title>
                    <Text type="secondary" style={{ fontSize: 16 }}>
                        Welcome back! Please sign in to continue.
                    </Text>
                </div>

                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: 24, borderRadius: 8 }}
                        closable
                        onClose={() => setError('')}
                    />
                )}

                <Form
                    name="login"
                    onFinish={onFinish}
                    layout="vertical"
                    size="large"
                >
                    <Form.Item
                        name="username"
                        rules={[
                            { required: true, message: 'Please enter your username!' },
                            { min: 3, message: 'Username must be at least 3 characters!' }
                        ]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                            placeholder="Username"
                            style={{ borderRadius: 8, height: 48 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: 'Please enter your password!' },
                            { min: 6, message: 'Password must be at least 6 characters!' }
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                            placeholder="Password"
                            style={{ borderRadius: 8, height: 48 }}
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 16 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            style={{
                                height: 48,
                                borderRadius: 8,
                                fontSize: 16,
                                fontWeight: 500
                            }}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </Button>
                    </Form.Item>
                </Form>

                <Divider style={{ margin: '24px 0' }}>
                    <Text type="secondary">New to Galaxy Mentor?</Text>
                </Divider>

                <div style={{ textAlign: 'center' }}>
                    <Link to="/register">
                        <Button
                            type="default"
                            block
                            style={{
                                height: 48,
                                borderRadius: 8,
                                fontSize: 16,
                                borderColor: '#1890ff',
                                color: '#1890ff'
                            }}
                        >
                            Create Account
                        </Button>
                    </Link>
                </div>
            </Card>
        </div>
    );
};

export default Login;
